"use client";

import React, { useCallback, useMemo, useEffect, useRef, useState, memo } from "react";

import { strings } from "@perceptron/constants";
import { VideoPlayer, VideoPlayerHandle, TCollection, TTimelineItem, focusTargetId } from "@perceptron/core";
import {
  AnnotationSource,
  isClipAnnotation,
  isTimeBoxAnnotation,
  isTimedPointAnnotation,
  isTimedPolygonAnnotation,
  isTrackAnnotation,
} from "@perceptron/core/types";
import { Button } from "@perceptron/ui/components/button";
import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";
import { getContentTypeFromExtension } from "@perceptron/utils/mime";
import { VideoMimeType } from "@vidstack/react";
import { Drawer } from "vaul";

import { DEMO_NANO_MODEL_URLS } from "@demo/constants/tracking";
import AgentOutput from "@demo/features/agent-output";
import { isPinnedVideoAnnotation, useAnnotationInteractions } from "@demo/hooks/annotations/useAnnotationInteractions";
import { getTrackingRecoveryProvider } from "@demo/libs/tracking-recovery";
import { useIsDevModeActive } from "@demo/store/dev-mode";
import { useDisplayedMediaIndex, useMediaCarouselStore } from "@demo/store/media-carousel";
import { useModelStore } from "@demo/store/model";

import { annotationToTimelineItem } from "./timelineItems";

const getVideoDuration = (url: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.src = url;

    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };

    video.onerror = () => {
      reject("Error loading video metadata");
    };
  });
};

// Cache durations across remounts to avoid layout shift.
const durationCache: Record<string, number> = {};
const ANNOTATIONS_COLLECTION_ID = "annotations-collection";

interface VideoOutputProps {
  /** Width of the filmstrip rail overlaying the panel's left edge, px. */
  insetLeft?: number;
  /** Height of the rail overlaying the top edge (mobile). */
  insetTop?: number;
  /** Lets background warming yield while this player has work in flight. */
  onTrackingBusyChange?: (busy: boolean) => void;
}

const VideoOutput = memo(({ insetLeft = 0, insetTop = 0, onTrackingBusyChange }: VideoOutputProps) => {
  const developerMode = useIsDevModeActive();
  const media = useMediaCarouselStore((state) => state.mediaInAgentCarousel);
  const currentMediaIndex = useDisplayedMediaIndex();
  const currentMedia = useMemo(() => media[currentMediaIndex], [media, currentMediaIndex]);
  const outputRunContext = useModelStore((state) => state.outputRunContext);
  const recoveryProvider = useMemo(
    () => getTrackingRecoveryProvider(outputRunContext, currentMedia?.key),
    [outputRunContext, currentMedia?.key],
  );

  const videoPlayerRef = useRef<VideoPlayerHandle>(null);
  // An existing pin on mount is state to restore, not a new navigation action.
  const lastClickedAnnotationId = useRef(useModelStore.getState().clickedAnnotationId);
  const syncedSelection = useRef<{
    player: VideoPlayerHandle;
    source: string | undefined;
    id: string | null;
  } | null>(null);
  const syncedHighlight = useRef<{
    player: VideoPlayerHandle;
    source: string | undefined;
    ids: string[] | undefined;
  } | null>(null);
  const [agentSheetOpen, setAgentSheetOpen] = useState(false);

  const handleAgentSheetOpenChange = useCallback((open: boolean) => {
    setAgentSheetOpen(open);
    videoPlayerRef.current?.setMinimized(open);
  }, []);

  // Durations are held per asset rather than as one value: a single value
  // carries the previous video's length into the next one until its own probe
  // resolves, laying the timeline out against an asset that is no longer on
  // screen. Keyed this way an asset either has its own duration or none yet,
  // and none means 0 — which the player reads as "take it from the media" —
  // rather than a number belonging to a different video.
  const mediaKey = currentMedia?.key;
  const mediaUrl = currentMedia?.url;
  const [durations, setDurations] = useState<Record<string, number>>(() => ({ ...durationCache }));
  const videoDuration = (mediaKey ? durations[mediaKey] : undefined) ?? 0;

  useEffect(() => {
    if (!mediaUrl || !mediaKey) return;

    const cached = durationCache[mediaKey];
    if (cached) {
      setDurations((current) => (current[mediaKey] === cached ? current : { ...current, [mediaKey]: cached }));
      return;
    }

    let cancelled = false;
    getVideoDuration(mediaUrl)
      .then((duration) => {
        durationCache[mediaKey] = duration;
        if (!cancelled) setDurations((current) => ({ ...current, [mediaKey]: duration }));
      })
      .catch((err) => console.error(err));
    return () => {
      cancelled = true;
    };
  }, [mediaKey, mediaUrl]);

  const {
    visibleAnnotations,
    visibleAnnotationIds,
    findAnnotationById,
    clickedAnnotationId,
    highlightedAnnotationIds,
    setHighlightedAnnotationIds,
    setClickedAnnotationId,
  } = useAnnotationInteractions();

  /**
   * Whether an id names an annotation on the asset this player is showing.
   * Waypoint, frame and cluster ids all resolve to their track first.
   */
  const ownsAnnotation = useCallback(
    (id: string) => visibleAnnotationIds.has(focusTargetId(id)),
    [visibleAnnotationIds],
  );

  useEffect(() => {
    const player = videoPlayerRef.current;
    if (!player) return;
    // A child source/data reset may already have cleared this render's selection.
    if (useModelStore.getState().clickedAnnotationId !== clickedAnnotationId) return;
    const annotation = clickedAnnotationId ? findAnnotationById(clickedAnnotationId) : undefined;
    const newClick = clickedAnnotationId !== lastClickedAnnotationId.current;
    if (annotation || !clickedAnnotationId) lastClickedAnnotationId.current = clickedAnnotationId;
    // The synthetic root has no model annotation. Keep its player-owned lock
    // through hover/streaming updates, until a new selection or source replaces it.
    const localCollectionId =
      !clickedAnnotationId &&
      syncedSelection.current?.player === player &&
      syncedSelection.current.source === currentMedia?.url &&
      syncedSelection.current.id === ANNOTATIONS_COLLECTION_ID
        ? ANNOTATIONS_COLLECTION_ID
        : null;
    const selectedId = isPinnedVideoAnnotation(annotation) ? annotation!.id : localCollectionId;
    if (syncedSelection.current?.player !== player || syncedSelection.current.id !== selectedId) {
      syncedSelection.current = { player, source: currentMedia?.url, id: selectedId };
      player.setSelectedSegmentId(selectedId);
    }
    if (!newClick) return;
    if (!annotation) return;

    // Player-origin selections update lastClickedAnnotationId in their callback,
    // so only a new output activation seeks. Streamed waypoint updates do not.
    if (isTrackAnnotation(annotation)) {
      const firstTime = annotation.waypoints.reduce(
        (earliest, { timestamp }) =>
          Number.isFinite(timestamp.start) && timestamp.start >= 0 ? Math.min(earliest, timestamp.start) : earliest,
        Infinity,
      );
      if (Number.isFinite(firstTime)) player.seekTo(firstTime);
    } else if (
      isClipAnnotation(annotation) ||
      isTimeBoxAnnotation(annotation) ||
      isTimedPointAnnotation(annotation) ||
      isTimedPolygonAnnotation(annotation)
    ) {
      player.seekTo(annotation.timestamp.start);
    }
  }, [clickedAnnotationId, findAnnotationById, videoDuration, currentMedia?.url]);

  useEffect(() => {
    const player = videoPlayerRef.current;
    if (!player) return;
    const ids = useModelStore.getState().highlightedAnnotationIds;
    if (
      syncedHighlight.current?.player === player &&
      syncedHighlight.current.source === currentMedia?.url &&
      syncedHighlight.current.ids === ids
    ) {
      return;
    }
    syncedHighlight.current = { player, source: currentMedia?.url, ids };
    // Only this asset's own annotations reach the player. Focus ids are not
    // asset-qualified — hovering a chip for another asset sets them globally —
    // so without this the player is asked to highlight segments it does not
    // have, and its timeline ends up reflecting another asset's response.
    player.setHighlightedSegmentIds((ids ?? []).filter(ownsAnnotation));
  }, [highlightedAnnotationIds, videoDuration, currentMedia?.url, ownsAnnotation]);

  useEffect(() => {
    const clearSelection = () => {
      const state = useModelStore.getState();
      const selected = state.clickedAnnotationId ? findAnnotationById(state.clickedAnnotationId) : undefined;
      const localCollectionPinned = syncedSelection.current?.id === ANNOTATIONS_COLLECTION_ID;
      if (!isPinnedVideoAnnotation(selected) && !localCollectionPinned) return;
      if (localCollectionPinned) videoPlayerRef.current?.setSelectedSegmentId(null);
      state.setClickedAnnotationId(undefined, undefined);
      state.setHighlightedAnnotationIds(undefined, undefined);
    };
    const clearOutsideSelection = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest("[data-video-player], [data-video-annotation-control]")) {
        return;
      }
      clearSelection();
    };
    const clearOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) clearSelection();
    };
    document.addEventListener("pointerdown", clearOutsideSelection);
    document.addEventListener("keydown", clearOnEscape);
    return () => {
      document.removeEventListener("pointerdown", clearOutsideSelection);
      document.removeEventListener("keydown", clearOnEscape);
    };
  }, [findAnnotationById]);

  const timelineData: TTimelineItem[] = useMemo(() => {
    if (!videoDuration) return [];

    const videoTrack: TTimelineItem = {
      type: "video",
      id: "video-track",
      label: strings.contentLowercase.video,
      segments: [
        {
          type: "clip",
          id: "segment-1",
          start: 0,
          end: videoDuration,
          label: "",
        },
      ],
    };

    if (!visibleAnnotations.length) return [videoTrack];

    const annotationsCollection: TCollection = {
      id: ANNOTATIONS_COLLECTION_ID,
      type: "collection",
      label: strings.annotationSidebar.title,
      children: visibleAnnotations
        .map((a) => annotationToTimelineItem(a))
        .filter((n): n is TTimelineItem => n !== null) as TCollection["children"],
    };

    return [videoTrack, annotationsCollection];
  }, [videoDuration, visibleAnnotations]);

  if (!currentMedia?.url) {
    return null;
  }

  const hasAnnotations = visibleAnnotations.length > 0;

  return (
    // The player owns its own frame and a left-hand label pane, so it is inset
    // past the filmstrip rail rather than running under it.
    <div
      className={cn(
        "flex h-full w-full flex-col gap-2 rounded-2xl",
        // Flush against the rail on whichever edge it occupies.
        insetLeft > 0 && "rail-docked-left",
        insetTop > 0 && "rail-docked-top",
      )}
      style={{ paddingLeft: insetLeft, paddingTop: insetTop }}
    >
      <div
        className={cn("w-full transition-[height] duration-700 md:h-full", {
          "h-1/2": agentSheetOpen || !hasAnnotations,
          "h-full": !agentSheetOpen && hasAnnotations,
        })}
      >
        <VideoPlayer
          developerMode={developerMode}
          initialDuration={videoDuration}
          nanoModelUrls={DEMO_NANO_MODEL_URLS}
          recoveryProvider={recoveryProvider}
          ref={videoPlayerRef}
          src={{
            src: currentMedia?.url,
            type: getContentTypeFromExtension(currentMedia?.extension) as VideoMimeType,
          }}
          timelineData={timelineData}
          onHighlightedSegmentIdsChange={(segmentIds) => {
            // Skip echoes and infer source from the first id so reasoning/content filtering stays correct across surfaces.
            const state = useModelStore.getState();
            const current = state.highlightedAnnotationIds ?? [];
            // A freshly mounted player reports an empty highlight set before it
            // has been told what is highlighted. That is a mount artifact, not
            // the viewer clearing anything — writing it back wipes a hover the
            // host just set. It shows up when hovering a video's annotation
            // while an image is on screen: the peek switches asset, the player
            // mounts, and the highlight disappears under the cursor.
            const playerKnowsHighlight =
              syncedHighlight.current?.player === videoPlayerRef.current &&
              syncedHighlight.current?.source === currentMedia?.url;
            if (segmentIds.length === 0 && current.length > 0 && !playerKnowsHighlight) return;
            const next = segmentIds.length ? segmentIds : undefined;
            const nextArr = next ?? [];
            const isEcho = current.length === nextArr.length && current.every((id, i) => id === nextArr[i]);
            // Preserve the player's row/bar provenance when the model reflects
            // this event. A fresh same-ID array from AgentOutput is still a new hover.
            if (videoPlayerRef.current) {
              syncedHighlight.current = {
                player: videoPlayerRef.current,
                source: currentMedia?.url,
                ids: isEcho ? state.highlightedAnnotationIds : next,
              };
            }
            if (isEcho) return;

            let inferredSource: AnnotationSource | undefined;
            if (nextArr.length > 0) {
              const firstId = nextArr[0]!;
              const baseId = firstId.split("::")[0] ?? firstId;
              inferredSource = findAnnotationById(baseId)?.source;
            }

            setHighlightedAnnotationIds(next, inferredSource);
          }}
          onSegmentSelect={(id) => {
            const annotation = id ? findAnnotationById(id) : undefined;
            const selected = isPinnedVideoAnnotation(annotation) ? annotation : undefined;
            if (videoPlayerRef.current) {
              syncedSelection.current = {
                player: videoPlayerRef.current,
                source: currentMedia?.url,
                id: id ?? null,
              };
            }
            const state = useModelStore.getState();
            // Imperative selection sync emits the same callback as a player click.
            if (state.clickedAnnotationId === selected?.id) return;
            const current = state.clickedAnnotationId ? findAnnotationById(state.clickedAnnotationId) : undefined;
            if (!selected && current && !isPinnedVideoAnnotation(current)) return;
            // Switching source scopes can remove the old player selection before
            // this render's new text click has been synced to the player.
            if (
              !selected &&
              isPinnedVideoAnnotation(current) &&
              state.clickedAnnotationId !== lastClickedAnnotationId.current
            ) {
              return;
            }
            // Player-origin focus only synchronizes selection; it never requests
            // temporal navigation when the shared state updates.
            lastClickedAnnotationId.current = selected?.id;
            setClickedAnnotationId(selected?.id, selected?.source);
            setHighlightedAnnotationIds(undefined, undefined);
          }}
          onTrackingBusyChange={onTrackingBusyChange}
        />
      </div>

      {hasAnnotations ? (
        <Drawer.Root modal={false} open={agentSheetOpen} onOpenChange={handleAgentSheetOpenChange}>
          <Drawer.Trigger asChild>
            <Button
              className={`w-full justify-between rounded-2xl bg-background-alt-2 px-4 hover:bg-background-alt-2 md:hidden ${
                agentSheetOpen ? "hidden" : ""
              }`}
              variant="ghost"
            >
              <span className="text-foreground/80 text-sm">{strings.agentOutput.mobileSheetTrigger}</span>
              <Icon className="text-foreground/60 h-4 w-4" name="chevron-up" />
            </Button>
          </Drawer.Trigger>
          <Drawer.Portal>
            <Drawer.Overlay className="pointer-events-none fixed inset-0 z-40 bg-transparent" />
            <Drawer.Content className="fixed inset-x-0 bottom-0 z-50 flex h-1/2 flex-col overscroll-contain rounded-t-2xl border-t border-white/10 bg-black outline-none">
              <div className="flex shrink-0 cursor-grab items-center justify-center py-3 active:cursor-grabbing">
                <div aria-hidden className="h-[3px] w-11 rounded-full bg-white/30" />
              </div>
              <Drawer.Title className="sr-only">{strings.agentOutput.mobileSheetTitle}</Drawer.Title>
              <div className="min-h-0 flex-1 overflow-hidden overscroll-contain">
                <AgentOutput className="overscroll-contain" />
              </div>
            </Drawer.Content>
          </Drawer.Portal>
        </Drawer.Root>
      ) : (
        <div className="min-h-0 w-full flex-1 md:hidden">
          <AgentOutput />
        </div>
      )}
    </div>
  );
});

VideoOutput.displayName = "VideoOutput";

export default VideoOutput;
